import { useState, useEffect, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { clientApi } from '../../services/client';
import extractApiError from '../../utils/extractApiError';

interface SiteFormData {
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  timezone: string;
}

const US_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

export default function ClientSiteForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [form, setForm] = useState<SiteFormData>({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    timezone: 'America/New_York',
  });
  const [error, setError] = useState<string | null>(null);
  const [formInitialized, setFormInitialized] = useState(false);

  // Load existing site if editing
  const { data: site, isLoading: siteLoading } = useQuery({
    queryKey: ['client', 'sites', id],
    queryFn: () => clientApi.getSite(id!).then((r) => r.data),
    enabled: isEdit,
  });

  // Populate form when site data loads (only on first load, not on refetch)
  useEffect(() => {
    if (site && !formInitialized) {
      setForm({
        name: site.name,
        address: site.address,
        city: site.city,
        state: site.state,
        zip_code: site.zip_code,
        timezone: site.timezone,
      });
      setFormInitialized(true);
    }
  }, [site, formInitialized]);

  const createMutation = useMutation({
    mutationFn: (data: SiteFormData) => clientApi.createSite(data),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => clientApi.updateSite(id!, data),
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ ...form });
        await queryClient.invalidateQueries({ queryKey: ['client', 'sites'] });
        navigate(`/client/sites/${id}`);
      } else {
        const response = await createMutation.mutateAsync(form);
        await queryClient.invalidateQueries({ queryKey: ['client', 'sites'] });
        navigate(`/client/sites/${response.data.id}`);
      }
    } catch (err: unknown) {
      setError(extractApiError(err));
    }
  };

  if (isEdit && siteLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <PageHeader
        title={isEdit ? 'Edit Site' : 'Add Site'}
        description={isEdit ? `Editing ${site?.name}` : 'Register a new security site'}
      />

      <form onSubmit={handleSubmit} className="max-w-2xl">
        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Site Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
                Site Name *
              </label>
              <input
                id="name"
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="address" className="block text-sm font-medium text-slate-700 mb-1">
                Address *
              </label>
              <input
                id="address"
                type="text"
                required
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-slate-700 mb-1">
                City *
              </label>
              <input
                id="city"
                type="text"
                required
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label htmlFor="state" className="block text-sm font-medium text-slate-700 mb-1">
                State *
              </label>
              <input
                id="state"
                type="text"
                required
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label htmlFor="zip_code" className="block text-sm font-medium text-slate-700 mb-1">
                ZIP Code *
              </label>
              <input
                id="zip_code"
                type="text"
                required
                value={form.zip_code}
                onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-slate-700 mb-1">
                Timezone
              </label>
              <select
                id="timezone"
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {US_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz.replace('_', ' ').replace('America/', '').replace('Pacific/', '')}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={isSaving}
            className="px-6 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Site'}
          </button>
          <button
            type="button"
            onClick={() => navigate(isEdit ? `/client/sites/${id}` : '/client/sites')}
            className="px-6 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </>
  );
}
